import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IChartConfigApiModel } from '~/types/graph-builder'

import ActionMenu from '~/components/ActionMenu'
import ApplicationsListCellRenderer from '~/components/CellRenderers/ApplicationsListCellRenderer'
import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { AUTHORIZATIONS } from '~/const'
import { GRAPH_TYPE } from '~/const/graphs-builder'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { appKeyDeviceTypeToString } from '~/utils/application'
import { numberComparator } from '~/utils/comparators'
import { formatGraphTitle } from '~/utils/graphs-builder'
import { actionsColDef, applicationsListColDef, badgeColDef, dateIsoColDef, textColDef } from '~/utils/table'

export interface ITableConfig {
  handleDeleteConfig: (configId: string) => void
  handleEditConfig: (configId: string) => void
}

const ChartTitleCellRenderer = ({
  value,
}: ICellRendererParams<IChartConfigApiModel, IChartConfigApiModel['title']>): ReactElement => {
  return value ? (
    <div className="text">
      <span>{formatGraphTitle(value)}</span>
    </div>
  ) : (
    <div />
  )
}

const ActionsCellRenderer = ({
  context,
  data,
}: ICellRendererParams<IChartConfigApiModel, null, ITableConfig>): ReactElement => {
  const isUserAuthorized = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.WRITE])
  const { handleDeleteConfig, handleEditConfig } = context

  if (context && data) {
    const menu: Menu = {
      actions: [
        {
          action: () => handleEditConfig(data.graph_config_id),
          label: 'Edit',
          type: 'default',
        },
        {
          action: () => handleDeleteConfig(data.graph_config_id),
          label: 'Delete',
          type: 'default',
        },
      ],
    }

    return <ActionMenu disabled={!isUserAuthorized} menu={menu} placement="top" />
  }

  return <div />
}

const columns: ColDef<IChartConfigApiModel>[] = [
  textColDef(ChartTitleCellRenderer, {
    field: 'title',
    headerName: 'Title',
    minWidth: 350,
    suppressMovable: true,
  }),
  textColDef(TextCellRenderer, {
    field: 'page',
    headerName: 'Page',
    resizable: true,
    suppressMovable: true,
    width: 100,
  }),
  textColDef(TextCellRenderer, {
    field: 'section',
    headerName: 'Section',
    resizable: true,
    suppressMovable: true,
    width: 100,
  }),
  textColDef(TextCellRenderer, {
    comparator: numberComparator,
    field: 'position',
    filter: false,
    headerName: 'Position',
    maxWidth: 70,
    resizable: false,
    valueGetter: ({ data }: ValueGetterParams<IChartConfigApiModel>) => {
      if (data) {
        const { column, line, position, series } = data

        if (series.length === 1 && series[0].graph_type === GRAPH_TYPE.RAW) {
          return `${line} / ${column}`
        }

        return position
      }

      return ''
    },
    width: 100,
  }),
  badgeColDef(BadgeCellRenderer, {
    autoHeight: true,
    cellStyle: { overflow: 'scroll' },
    filterValueGetter: ({ data }: ValueGetterParams<IChartConfigApiModel>) =>
      data?.series.map(({ values }) => values).flat(),
    headerName: 'Values',
    minWidth: 210,
    resizable: true,
    valueGetter: ({ data }: ValueGetterParams<IChartConfigApiModel>) => {
      if (!data) return undefined

      return {
        data:
          [...new Set(data.series.map(({ values }) => values).flat())].map((v) => ({
            id: v,
            name: v,
          })) || [],
        type: 'default',
      }
    },
  }),
  badgeColDef(BadgeCellRenderer, {
    autoHeight: true,
    cellStyle: { overflow: 'scroll' },
    filterValueGetter: ({ data }: ValueGetterParams<IChartConfigApiModel>) =>
      data?.series.map(({ normalized_by }) => normalized_by).flat(),
    headerName: 'Normalized by',
    minWidth: 210,
    resizable: true,
    valueGetter: ({ data }: ValueGetterParams<IChartConfigApiModel>) => {
      if (!data) return undefined

      return {
        data:
          [...new Set(data.series.map(({ normalized_by }) => normalized_by).flat())].map((v) => ({
            id: v,
            name: v,
          })) || [],
        type: 'default',
      }
    },
  }),
  applicationsListColDef(ApplicationsListCellRenderer, {
    field: 'applications',
    filter: true,
    filterValueGetter: ({ data }: ValueGetterParams<IChartConfigApiModel>) =>
      data?.applications.length === 0
        ? ['(All apps)']
        : data?.applications.map(({ app_key, device_type }) => appKeyDeviceTypeToString(app_key, device_type)).flat(),
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    width: 150,
  }),
  dateIsoColDef({
    field: 'updated_at',
    headerName: 'Last edit',
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    width: 80,
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
